import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy } from "lucide-react";
import { supabase } from "../supabaseClient";
import {
  getTeamStyle,
  getTextColorForAccent,
  isDarkModeActive,
} from "../utils/teamColors";

// ── Layout constants ─────────────────────────────────────────────────────────
const TEAM_ROW = 36;
const VS_GAP = 20;
const MATCH_H = TEAM_ROW * 2 + VS_GAP;
const R1_GAP = 16;
const R1_UNIT = MATCH_H + R1_GAP;
const CANVAS_H = 8 * MATCH_H + 7 * R1_GAP;
const COL_W = 190;
const FINALS_COL_W = 220; // Wider for champion display
const PIPE_W = 36;

const r1Top = (i) => i * R1_UNIT;
const r1Cy = (i) => r1Top(i) + MATCH_H / 2;

const roundCy = (round, slot) => {
  if (round === 0) return r1Cy(slot);
  const span = Math.pow(2, round);
  const firstFeeder = slot * span;
  return (r1Cy(firstFeeder) + r1Cy(firstFeeder + span - 1)) / 2;
};
const roundTop = (round, slot) => roundCy(round, slot) - MATCH_H / 2;

// component round index (0=R1,1=QF,2=SF,3=Final) -> your DB's playoff_round (1-4)
const DB_ROUND_OFFSET = 1;

// ── SVG Connectors (unchanged) ───────────────────────────────────────────────
const ConnectorSVG = ({ pairs, color = "#d1d5db" }) => (
  <svg
    width={PIPE_W}
    height={CANVAS_H}
    className="flex-none"
    style={{ overflow: "visible" }}
  >
    {pairs.map(({ fromA, fromB, to }, i) => {
      const midX = PIPE_W / 2;
      return (
        <g key={i}>
          <line
            x1={0}
            y1={fromA + 30}
            x2={midX}
            y2={fromA + 30}
            stroke={color}
            strokeWidth={1.5}
          />
          <line
            x1={0}
            y1={fromB + 30}
            x2={midX}
            y2={fromB + 30}
            stroke={color}
            strokeWidth={1.5}
          />
          <line
            x1={midX}
            y1={fromA + 30}
            x2={midX}
            y2={fromB + 30}
            stroke={color}
            strokeWidth={1.5}
          />
          <line
            x1={midX}
            y1={to + 30}
            x2={PIPE_W}
            y2={to + 30}
            stroke={color}
            strokeWidth={1.5}
          />
        </g>
      );
    })}
  </svg>
);

const hasTie = (a, b) =>
  a && b && parseFloat(a.win_pct ?? 0) === parseFloat(b.win_pct ?? 0);

// ── Team slot ────────────────────────────────────────────────────────────────
// NEW: score, isWinner, isLoser props; seed chip only renders when seed is known
const TeamSlot = ({
  team,
  seed,
  isTied,
  score,
  isWinner,
  isLoser,
  onClick,
}) => {
  const ts = team ? getTeamStyle(team) : null;

  if (!team) {
    return (
      <div
        className="flex items-center gap-2 px-2.5 rounded-md border border-dashed border-gray-400 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30"
        style={{ height: TEAM_ROW }}
      >
        <span className="text-[11px] text-gray-400 dark:text-gray-500 italic">
          TBD
        </span>
      </div>
    );
  }

  // Compute readable text color against the accent background
  const slotTextColor = getTextColorForAccent(ts.accentHex, isDarkModeActive());

  return (
    <div
      className={`flex items-center gap-2 px-2.5 rounded-md border border-gray-500 dark:border-gray-700 cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-all group ${isLoser ? "opacity-60" : ""}`}
      style={{ height: TEAM_ROW, ...ts.accentStyle }}
      onClick={onClick}
    >
      {seed != null && (
        <div
          className="flex-none flex items-center justify-center rounded text-[10px] font-black"
          style={{
            width: 18,
            height: 18,
            border: `1.5px solid ${slotTextColor}`,
            color: slotTextColor,
            backgroundColor: "transparent",
          }}
        >
          {seed}
        </div>
      )}

      <div
        className="flex-none w-0.5 self-stretch rounded-full my-1.5"
        style={{ backgroundColor: slotTextColor, opacity: 0.3 }}
      />

      {team.logo_url ? (
        <img
          src={team.logo_url}
          alt={team.name}
          className="flex-none rounded object-cover"
          style={{ width: 18, height: 18 }}
        />
      ) : (
        <div
          className="flex-none rounded flex items-center justify-center text-[9px] font-black"
          style={{
            width: 18,
            height: 18,
            ...ts.headerStyle,
            color: getTextColorForAccent(ts.primaryHex, isDarkModeActive()),
          }}
        >
          {team.name
            .split(" ")
            .map((w) => w[0])
            .join("")
            .slice(0, 2)
            .toUpperCase()}
        </div>
      )}

      <span
        className="flex-1 min-w-0 text-xs font-semibold truncate group-hover:opacity-70 transition-opacity"
        style={{ color: slotTextColor }}
      >
        {team.name}
      </span>

      {isTied && (
        <span
          className="flex-none text-[9px] font-black text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded px-1"
          title="Tiebreaker applied: 1) Head-to-head record → 2) H2H point diff → 3) Overall point diff"
        >
          TB
        </span>
      )}

      {score != null && (
        <span
          className={`flex-none text-xs font-black tabular-nums ${isWinner ? "text-emerald-500" : "text-gray-400 dark:text-gray-500"}`}
        >
          {score}
        </span>
      )}
    </div>
  );
};

// ── Matchup block ────────────────────────────────────────────────────────────
const Matchup = ({
  topTeam,
  bottomTeam,
  topSeed,
  bottomSeed,
  topTied,
  bottomTied,
  topScore,
  bottomScore,
  topWinner,
  bottomWinner,
  onClick,
}) => (
  <div
    className="flex flex-col justify-between p-2 rounded-xl border border-gray-600 dark:border-gray-100/80 bg-white shadow-sm transition-all duration-200 hover:border-orange-500/50 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900/50 dark:hover:border-orange-500/40"
    style={{ height: MATCH_H + 15 }}
  >
    <div className="flex-1 flex flex-col justify-center">
      <TeamSlot
        team={topTeam}
        seed={topSeed}
        isTied={topTied}
        score={topScore}
        isWinner={topWinner}
        isLoser={bottomWinner}
        onClick={() => topTeam && onClick(topTeam.id)}
      />
    </div>

    <div
      className="flex items-center justify-center my-1 select-none"
      style={{ height: VS_GAP }}
    >
      <span className="px-2 text-[9px] font-black uppercase tracking-widest text-gray-600 dark:text-gray-400 bg-gray-400 dark:bg-gray-50 rounded dark:text-zinc-500 dark:bg-zinc-800">
        vs
      </span>
    </div>

    <div className="flex-1 flex flex-col justify-center">
      <TeamSlot
        team={bottomTeam}
        seed={bottomSeed}
        isTied={bottomTied}
        score={bottomScore}
        isWinner={bottomWinner}
        isLoser={topWinner}
        onClick={() => bottomTeam && onClick(bottomTeam.id)}
      />
    </div>
  </div>
);

const RoundColumn = ({ round, matchups, navigate }) => (
  <div
    className="relative flex-none"
    style={{ width: COL_W, height: CANVAS_H }}
  >
    {matchups.map((m, slot) => (
      <div
        key={slot}
        className="absolute left-0 right-0"
        style={{ top: roundTop(round, slot) }}
      >
        <Matchup {...m} onClick={(id) => navigate(`/teams/${id}`)} />
      </div>
    ))}
  </div>
);

const ChampionSlot = ({ champion }) => {
  if (!champion) {
    return (
      <div
        className="absolute left-0 right-0 flex items-center justify-center"
        style={{ top: "42%", height: TEAM_ROW + 20 }}
      >
        <div className="flex flex-col items-center gap-2 px-6 py-3 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30">
          <Trophy size={28} className="text-gray-400 dark:text-gray-600" />
          <span className="text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-500">
            Championship
          </span>
        </div>
      </div>
    );
  }

  const ts = getTeamStyle(champion);

  return (
    <div
      className="absolute left-0 right-0 flex items-center justify-center"
      style={{ top: "38%", minHeight: 100 }}
    >
      <div
        className="flex flex-col items-center gap-3 px-6 py-4 rounded-2xl border-4 shadow-2xl animate-pulse"
        style={{
          borderColor: ts.accentHex,
          background: `linear-gradient(135deg, ${ts.primaryHex}15, ${ts.secondaryHex || ts.primaryHex}15)`,
        }}
      >
        <Trophy size={40} className="text-yellow-500 drop-shadow-lg" />

        {champion.logo_url ? (
          <img
            src={champion.logo_url}
            alt={champion.name}
            className="w-16 h-16 rounded-xl object-cover border-2 shadow-lg"
            style={{ borderColor: ts.accentHex }}
          />
        ) : (
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-black text-white shadow-lg"
            style={ts.headerStyle}
          >
            {champion.name
              .split(" ")
              .map((w) => w[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </div>
        )}

        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] font-black uppercase tracking-widest text-yellow-600 dark:text-yellow-400">
            Champion
          </span>
          <span
            className="text-base font-black text-center"
            style={{ color: ts.accentHex }}
          >
            {champion.name}
          </span>
        </div>
      </div>
    </div>
  );
};

// ── Main PlayoffBracket export ───────────────────────────────────────────────
// NEW: seasonId prop drives the games fetch + realtime subscription
export const PlayoffBracket = ({ teams, seasonId }) => {
  const navigate = useNavigate();
  const [playoffGames, setPlayoffGames] = useState({}); // key: `${playoff_round}-${bracket_slot}`

  useEffect(() => {
    if (!seasonId) return;

    let cancelled = false;

    const fetchPlayoffGames = async () => {
      const { data, error } = await supabase
        .from("games")
        .select(
          "id, playoff_round, bracket_slot, team1_id, team2_id, team1_score, team2_score, status",
        )
        .eq("season_id", seasonId)
        .eq("game_type", "playoff")
        .order("playoff_round", { ascending: true })
        .order("bracket_slot", { ascending: true });

      if (error) {
        console.error("Failed to load playoff games:", error);
        return;
      }
      if (cancelled) return;

      const map = {};
      (data || []).forEach((g) => {
        map[`${g.playoff_round}-${g.bracket_slot}`] = g;
      });
      setPlayoffGames(map);
    };

    fetchPlayoffGames();

    const channel = supabase
      .channel(`playoff-bracket-${seasonId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "games",
          filter: `season_id=eq.${seasonId}`,
        },
        (payload) => {
          const g = payload.new;
          if (!g || g.game_type !== "playoff") return;
          setPlayoffGames((prev) => ({
            ...prev,
            [`${g.playoff_round}-${g.bracket_slot}`]: g,
          }));
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [seasonId]);

  const preparedTeams = (teams || []).map((team) => {
    const profile = team?.team_profiles?.[0] || team?.team_profile || {};
    return {
      ...team,
      primary_color: profile.primary_color || team.primary_color,
      secondary_color: profile.secondary_color || team.secondary_color,
      gradient: profile.gradient ?? team.gradient ?? false,
    };
  });

  const teamsById = useMemo(() => {
    const map = {};
    preparedTeams.forEach((t) => {
      map[String(t.id)] = t;
    });
    return map;
  }, [preparedTeams]);

  const seeded = [...preparedTeams].slice(0, 16);
  while (seeded.length < 16) seeded.push(null);

  const r1Order = [
    { hi: 1, lo: 16 },
    { hi: 8, lo: 9 },
    { hi: 4, lo: 13 },
    { hi: 5, lo: 12 },
    { hi: 3, lo: 14 },
    { hi: 6, lo: 11 },
    { hi: 2, lo: 15 },
    { hi: 7, lo: 10 },
  ];

  // Pulls the real game row for a given (component round, slot) and turns it
  // into renderable matchup data — team objects, scores, winner flags.
  const buildMatchup = (round, slot, seedInfo = {}) => {
    const game = playoffGames[`${round + DB_ROUND_OFFSET}-${slot}`];
    const topTeam =
      game?.team1_id != null ? teamsById[String(game.team1_id)] : null;
    const bottomTeam =
      game?.team2_id != null ? teamsById[String(game.team2_id)] : null;
    const isFinished = game?.status === "finished";
    const topScore = isFinished ? game.team1_score : null;
    const bottomScore = isFinished ? game.team2_score : null;
    const topWinner = isFinished && topTeam && topScore >= bottomScore;
    const bottomWinner = isFinished && bottomTeam && bottomScore > topScore;

    return {
      topTeam,
      bottomTeam,
      topSeed: seedInfo.topSeed ?? null,
      bottomSeed: seedInfo.bottomSeed ?? null,
      topTied: seedInfo.topTied ?? false,
      bottomTied: seedInfo.bottomTied ?? false,
      topScore,
      bottomScore,
      topWinner,
      bottomWinner,
    };
  };

  const r1 = r1Order.map(({ hi, lo }, slot) => {
    const hiIndex = hi - 1;
    const loIndex = lo - 1;
    const hiTeam = seeded[hiIndex];
    const loTeam = seeded[loIndex];

    const prevHi = hiIndex > 0 ? seeded[hiIndex - 1] : null;
    const nextHi = hiIndex < 15 ? seeded[hiIndex + 1] : null;
    const prevLo = loIndex > 0 ? seeded[loIndex - 1] : null;
    const nextLo = loIndex < 15 ? seeded[loIndex + 1] : null;

    const matchup = buildMatchup(0, slot, {
      topSeed: hi,
      bottomSeed: lo,
      topTied: hasTie(hiTeam, prevHi) || hasTie(hiTeam, nextHi),
      bottomTied: hasTie(loTeam, prevLo) || hasTie(loTeam, nextLo),
    });

    // Fall back to standings-based seeding if the game row hasn't loaded/exists yet
    return {
      ...matchup,
      topTeam: matchup.topTeam || hiTeam,
      bottomTeam: matchup.bottomTeam || loTeam,
    };
  });

  const qf = Array.from({ length: 4 }, (_, slot) => buildMatchup(1, slot));
  const sf = Array.from({ length: 2 }, (_, slot) => buildMatchup(2, slot));
  const final = buildMatchup(3, 0);

  const finalGame = playoffGames[`4-0`];
  const champion =
    finalGame?.status === "finished"
      ? final.topWinner
        ? final.topTeam
        : final.bottomTeam
      : null;

  const r1ToQf = Array.from({ length: 4 }, (_, i) => ({
    fromA: roundCy(0, 2 * i),
    fromB: roundCy(0, 2 * i + 1),
    to: roundCy(1, i),
  }));
  const qfToSf = Array.from({ length: 2 }, (_, i) => ({
    fromA: roundCy(1, 2 * i),
    fromB: roundCy(1, 2 * i + 1),
    to: roundCy(2, i),
  }));
  const sfToF = [
    { fromA: roundCy(2, 0), fromB: roundCy(2, 1), to: roundCy(3, 0) },
  ];

  return (
    <div className="p-3 md:p-4 lg:p-6">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4 text-[11px] text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-black text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded px-1">
            TB
          </span>
          <span>
            Tiebreaker applied — H2H record → H2H point diff → Overall point
            diff
          </span>
        </div>
        <span className="text-gray-300 dark:text-gray-600">·</span>
        <span>Seeding based on current regular season standings</span>
      </div>

      <div className="overflow-x-auto overflow-y-hidden pb-2 -mx-3 px-3 md:mx-0 md:px-0">
        <div
          className="relative flex items-start text-gray-200 dark:text-gray-700"
          style={{
            minWidth: "fit-content",
            width: (COL_W + PIPE_W) * 3 + COL_W + PIPE_W + FINALS_COL_W,
          }}
        >
          <div className="flex-none" style={{ width: COL_W }}>
            <p className="text-center text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
              Round 1
            </p>
            <RoundColumn round={0} matchups={r1} navigate={navigate} />
          </div>

          <ConnectorSVG pairs={r1ToQf} />

          <div className="flex-none" style={{ width: COL_W }}>
            <p className="text-center text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
              Quarterfinals
            </p>
            <RoundColumn round={1} matchups={qf} navigate={navigate} />
          </div>

          <ConnectorSVG pairs={qfToSf} />

          <div className="flex-none" style={{ width: COL_W }}>
            <p className="text-center text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
              Semifinals
            </p>
            <RoundColumn round={2} matchups={sf} navigate={navigate} />
          </div>

          <ConnectorSVG pairs={sfToF} />

          <div className="relative flex-none" style={{ width: FINALS_COL_W }}>
            <p className="text-center text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
              Finals
            </p>
            <div
              className="absolute left-0 right-0"
              style={{ top: roundTop(3, 0) }}
            >
              <Matchup {...final} onClick={(id) => navigate(`/teams/${id}`)} />
            </div>
            <ChampionSlot champion={champion} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayoffBracket;
